import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Check, Flag, Send } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const stCls = (st) => st === "approved" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : st === "challenged" ? "text-red-700 bg-red-50 border-red-200"
  : st === "submitted" ? "text-amber-700 bg-amber-50 border-amber-200"
  : "text-zinc-500 bg-zinc-50 border-zinc-200";

export default function SignoffPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/signoffs`); setData(data); }
    catch { /* non-blocking */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); try { const { data } = await fn(); setData(data); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };
  const submit = (k) => act(() => api.post(`/admin/enterprise360/clients/${clientId}/signoffs/${k}/submit`));
  const approve = (k) => act(() => api.post(`/admin/enterprise360/clients/${clientId}/signoffs/${k}/decide`, { decision: "approved" }));
  const challenge = (k) => {
    const reason = window.prompt("Challenge — provide counter-evidence / reason:");
    if (!reason) return;
    act(() => api.post(`/admin/enterprise360/clients/${clientId}/signoffs/${k}/decide`, { decision: "challenged", reason }));
  };

  const artefacts = data?.artefacts || [];
  const s = data?.signoffs || {};
  const r = data?.readiness || {};
  return (
    <div data-testid="signoff-panel" className="border border-zinc-200 rounded-lg p-4 lg:col-span-2">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1"><ShieldCheck size={13} /> Four-eyes sign-off</div>
        {data && <span data-testid="signoff-readiness" className={`text-[11px] rounded-full px-2.5 py-0.5 border font-semibold ${r.ready ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{r.ready ? "Cleared to publish" : `${(r.pending || []).length} awaiting sign-off`}</span>}
      </div>
      <p className="text-[12px] text-zinc-500 mb-3">Each artefact must be approved by a reviewer who did not submit it — or challenged with counter-evidence. Every action is written to the sealed evidence trail.</p>
      <div className="divide-y divide-zinc-100">
        {artefacts.map((a) => {
          const v = s[a.key] || {};
          return (
            <div key={a.key} data-testid={`signoff-row-${a.key}`} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-[200px]">
                <div className="text-[13px] text-zinc-800 font-medium">{a.label}</div>
                <div className="text-[11px] text-zinc-400">
                  {v.status === "draft" && "Not submitted"}
                  {v.status === "submitted" && `Submitted by ${v.submitted_by}`}
                  {v.status === "approved" && `Approved by ${v.reviewer}`}
                  {v.status === "challenged" && `Challenged by ${v.reviewer}: ${v.reason}`}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span data-testid={`signoff-status-${a.key}`} className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${stCls(v.status)}`}>{v.status}</span>
                {(v.status === "draft" || v.status === "challenged") && (
                  <button data-testid={`signoff-submit-${a.key}`} onClick={() => submit(a.key)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2.5 py-1 rounded disabled:opacity-40"><Send size={11} /> Submit</button>
                )}
                {v.status === "submitted" && (
                  <>
                    <button data-testid={`signoff-approve-${a.key}`} onClick={() => approve(a.key)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-white bg-emerald-600 px-2.5 py-1 rounded disabled:opacity-40"><Check size={11} /> Approve</button>
                    <button data-testid={`signoff-challenge-${a.key}`} onClick={() => challenge(a.key)} disabled={busy} className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-red-600 border border-red-300 px-2.5 py-1 rounded disabled:opacity-40"><Flag size={11} /> Challenge</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
